import React, { useEffect, useState } from "react";
import { Box, Button, Divider, FormLabel, Grid, IconButton, Stack, TextField, Toolbar, Tooltip } from "@mui/material";
import { Lesson } from "../../types/Lessons";
import FishboneChart from "../Charts/FishboneChart";
import dayjs from "dayjs";
import ImpactChart from "../Charts/ImpactChart";
import ShareIcon from '@mui/icons-material/Share';
import CodeIcon from '@mui/icons-material/Code';
import ActionMenu from "./Menu";
import EditIcon from '@mui/icons-material/Edit';
import { EditableTextField } from "../Shared/FormItems";
import Questionaire from "./Questionaire";
import { ChangeProps } from "./Show";
import Toolbox from "../Shared/Toolbox";

const Summary = (props: { lesson: Lesson, animated?: boolean, preview?: boolean, editable?: boolean } & ChangeProps ) => {

  const animatedClass = props.animated ? "animate__animated" : ""

  const [copyLink, shouldCopyLink] = useState<boolean>(false);
  const [editSummary, setEditSummary] = useState<boolean>(false);
  const [editTitle, setEditTitle] = useState<boolean>(false);
  const [editOrigin, setEditOrigin] = useState<boolean>(false);
  const [editRiskScore, setEditRiskScore] = useState<boolean>(false);
  const [canEdit, shouldEdit] = useState<boolean>(false);

  useEffect(() => {
    if (typeof(props.lesson) === "undefined" || !copyLink || !props.lesson.url) { return }
    navigator.clipboard.writeText(props.lesson.url);
  }, [copyLink]);

  const shareDialog = () => {
    if (typeof(props.lesson) === "undefined") { return }
    if (typeof(navigator.share) !== "undefined") {
      navigator.share({url: props.lesson.url}).then(fulfilled => {
        if (typeof(fulfilled) === "undefined") {
          shouldCopyLink(true);
        }
      }).catch(rejected => shouldCopyLink(true));
      return;
    }
  }

  return <Grid container spacing={3}>
    <Grid size={12}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <FormLabel>Lesson learned from {dayjs.unix(props.lesson.created_at).format("ll")} / Last updated: {dayjs.unix(props.lesson.updated_at).format("lll")}</FormLabel>

          <Stack direction="row" alignItems="center" gap={0.5}>
            { editTitle ? <EditableTextField
                                     placeholder="Enter a title for the lesson"
                                     rows={1}
                                     onChange={value => props.onChange({headline: value})}
                                     onSave={value => {
                                        props.onSave({headline: value});
                                        setEditTitle(false);
                                     }}
                                     defaultValue={props.lesson.headline}
                                     fullWidth /> : <h3 style={{marginBottom: 0}}>{props.lesson.headline}</h3> }
            { canEdit && <IconButton color="info" size="small" onClick={() => setEditTitle(true)}><EditIcon fontSize="small" /></IconButton> }
          </Stack>

          <Stack direction="row" alignItems="center" gap={0.5}>
            { editOrigin ? <EditableTextField
                                     placeholder="Enter affected customer or project"
                                     rows={1}
                                     onChange={value => props.onChange({origin: value})}
                                     onSave={value => {
                                        props.onSave({origin: value});
                                        setEditOrigin(false);
                                     }}
                                     defaultValue={props.lesson.origin}
                                     fullWidth /> : <FormLabel>{props.lesson.origin}</FormLabel> }
            { canEdit && <>{ !props.lesson.origin && !editOrigin && <FormLabel>Customer or project</FormLabel> } <IconButton color="info" size="small" onClick={() => setEditOrigin(true)}><EditIcon fontSize="small" /></IconButton></> }
          </Stack>
        </Box>

        <Toolbar disableGutters>
        { !props.preview && <>
          <Tooltip 
            title={`Link copied`} 
            open={copyLink}>
              <Button
                size="small"
                variant="text"
                color="secondary"
                startIcon={<ShareIcon />}
                onClick={shareDialog}>Share</Button>
          </Tooltip>

          <ActionMenu {...props} onEdit={() => shouldEdit(true)} />
        </> }
        </Toolbar>
      </Stack>
      <Divider sx={{mt: 1}} />
    </Grid>

    { (!props.preview || props.lesson.summary) && <Grid size={{xs: 12, md: 6}} className={`${animatedClass} animate__fadeInUp`}>
      <Stack direction="row" alignItems="center" gap={0.5} mb={2}>
        <strong>Executive summary</strong>
        { canEdit && <IconButton color="info" size="small" onClick={() => setEditSummary(true)}><EditIcon fontSize="small" /></IconButton> }
      </Stack>
      
      { editSummary ? <EditableTextField placeholder="Summarize the issue in a few sentences."
                          defaultValue={props.lesson.summary}
                          rows={10}
                          onChange={value => props.onChange({summary: value})}
                          onSave={value => {
                            props.onSave({summary: value});
                            setEditSummary(false);
                          }} /> : <p>{props.lesson.summary}</p> }
    </Grid> }

    { (!props.preview || (props.lesson.impact_score && props.lesson.frequency_score)) && <Grid size={{xs: 12, md: !props.preview || props.lesson.summary ? 6 : 12}} className={`${animatedClass} animate__fadeInUp`}>
      <Stack direction="row" alignItems="center" gap={0.5} mb={2}>
        <strong>Risk score</strong>
        { canEdit && <IconButton color="info" size="small" onClick={() => setEditRiskScore(true)}><EditIcon fontSize="small" /></IconButton> }
      </Stack>
      
      <ImpactChart series={[props.lesson.impact_score && props.lesson.frequency_score ? [props.lesson.impact_score, props.lesson.frequency_score] : []]} height={300} />
      { editRiskScore && <Toolbox title="Edit risk score" onClose={() => setEditRiskScore(false)}><Questionaire lesson={props.lesson}
                                                                                                                persisted
                                                                                                                onCompletion={object => {
                                                                                                                  props.onSave(object);
                                                                                                                  setEditRiskScore(false);
                                                                                                                }} /></Toolbox> }
    </Grid> }

    { props.lesson.causes.length > 0 && <Grid size={12}>
      <Stack direction="row" alignItems="center" gap={0.5} mb={2}>
        <strong>Root cause analysis</strong>
      </Stack>
      <br />
      <FishboneChart {...props.lesson} />
    </Grid> }
    
  </Grid>
}

export default Summary;